import { Action, createReducer, on } from '@ngrx/store';
import { TransfersActions } from '@app/client/account/admin/transfers/actions';
import { ListReceiptsOutput, ListTransfersOutput } from '@smash-sdk/transfer/ejs/01-2024';

export const transfersFeatureKey = 'transfers';

export interface State {
  transfers: ListTransfersOutput['transfers'] & { team: string, receiversNumber: number, sender: string, receiverEmails: string, type: string }[];
  transfersLoading: boolean;
  transfersError: any;

  transfersReceived: ListReceiptsOutput['receipts'];
  transfersReceivedLoading: boolean;
  transfersReceivedError: any;

  transferDetails: any;
  transferDetailsFiles: any[];
  transferEvents: any[];
  transferEventsGroupedByEmail: any[];
  transferEventsGroupedByAnonymousUser: any[];
  loadingTransferDetails: boolean;
  loadingTransferDetailsFiles: boolean;
  loadingTransferEvents: boolean;

  transferReceivedDetails: any;
  transferReceivedDetailsLoading: boolean;
  transferReceivedDetailsLoaded: boolean;
  transferReceivedDetailsError: any;
  transferReceivedDetailsFiles: any[];

  transferReceivedDetailsFilesLoading: boolean;
  transferReceivedDetailsFilesLoaded: boolean;
  transferReceivedDetailsFilesError: any;

  transferReceivedDeleting: boolean;
  transferReceivedDeleted: boolean;
  transferReceivedDeletingError: any;

  transferDeleting: boolean;
  transferDeleted: boolean;
  transferDeletingError: any;
}

export const initialState: State = {
  transfers: null,
  transfersLoading: false,
  transfersError: null,

  transfersReceived: [],
  transfersReceivedLoading: false,
  transfersReceivedError: null,

  transferDetails: null,
  loadingTransferDetails: null,
  transferDetailsFiles: [],

  loadingTransferDetailsFiles: null,
  transferEvents: [],
  loadingTransferEvents: null,
  transferEventsGroupedByEmail: [],
  transferEventsGroupedByAnonymousUser: [],

  transferReceivedDetails: null,
  transferReceivedDetailsLoading: false,
  transferReceivedDetailsLoaded: false,
  transferReceivedDetailsError: null,
  transferReceivedDetailsFiles: [],
  transferReceivedDetailsFilesLoading: false,
  transferReceivedDetailsFilesLoaded: false,
  transferReceivedDetailsFilesError: null,

  transferReceivedDeleting: false,
  transferReceivedDeleted: false,
  transferReceivedDeletingError: null,

  transferDeleting: false,
  transferDeleted: false,
  transferDeletingError: null,
};



const transfersReducer = createReducer(
  initialState,
  on(TransfersActions.loadTransfers, (state) => ({
    ...state,
    transfersLoading: true,
  })),
  on(TransfersActions.loadTransfersSuccess, (state, { transfers }) => ({
    ...state,
    transfers: [...transfers],
    transfersLoading: false,
    transfersError: null
  })),
  on(TransfersActions.loadTransfersFailure, (state, { error }) => ({
    ...state,
    transfersLoading: false,
    transfersError: error
  })),

  on(TransfersActions.loadTransferDetails, (state) => ({
    ...state,
    transferDetails: null,
    loadingTransferDetails: true
  })),
  on(TransfersActions.loadTransferDetailsSuccess, (state, { transfer }) => ({
    ...state,
    transferDetails: transfer,
    loadingTransferDetails: false
  })),
  on(TransfersActions.loadTransferDetailsFailure, (state, { error }) => ({
    ...state,
    error,
    loadingTransferDetails: false
  })),
  on(TransfersActions.loadTransferDetailsFiles, (state) => ({
    ...state,
    transferDetailsFiles: null,
    loadingTransferDetailsFiles: true
  })),
  on(TransfersActions.loadTransferDetailsFilesSuccess, (state, { transferFiles }) => ({
    ...state,
    transferDetailsFiles: transferFiles,
    loadingTransferDetailsFiles: false
  })),
  on(TransfersActions.loadTransferDetailsFilesFailure, (state, { error }) => ({
    ...state,
    error,
    loadingTransferDetailsFiles: false
  })),
  on(TransfersActions.loadTransferEvents, (state) => ({
    ...state,
    transferEvents: null,
    transferEventsGroupedByEmail: [],
    transferEventsGroupedByAnonymousUser: [],
    loadingTransferEvents: true
  })),
  on(TransfersActions.loadTransferEventsSuccess, (state, { events, eventsGroupedByEmailArray, eventsGroupedByAnonymousUserArray }) => ({
    ...state,
    transferEvents: events,
    transferEventsGroupedByEmail: eventsGroupedByEmailArray,
    transferEventsGroupedByAnonymousUser: eventsGroupedByAnonymousUserArray,
    loadingTransferEvents: false
  })),
  on(TransfersActions.loadTransferEventsFailure, (state, { error }) => ({
    ...state,
    error,
    loadingTransferEvents: false
  })),
  on(TransfersActions.loadTransferDetailsFilesSuccess, (state, { transferFiles }) => ({
    ...state,
    transferDetailsFiles: transferFiles,
    loadingTransferDetailsFiles: false
  })),
  on(TransfersActions.loadTransferDetailsFilesFailure, (state, { error }) => ({
    ...state,
    error,
    loadingTransferDetailsFiles: false
  })),
  on(TransfersActions.deleteTransfer, (state) => ({
    ...state,
    transferDeleting: true,
    transferDeleted: initialState.transferDeleted,
    transferDeletingError: initialState.transferDeletingError,
  })),
  on(TransfersActions.deleteTransferSuccess, (state) => ({
    ...state,
    transferDeleting: initialState.transferDeleting,
    transferDeleted: true,
  })),
  on(TransfersActions.deleteTransferFailure, (state, { error }) => ({
    ...state,
    transferDeletingError: error,
    transferDeleting: initialState.transferDeleting,
  })),

  on(TransfersActions.listTransfersReceived, (state) => ({
    ...state,
    transfersReceivedLoading: true,
  })),
  on(TransfersActions.listTransfersReceivedSuccess, (state, { transfers }) => ({
    ...state,
    transfersReceived: transfers,
    transfersReceivedLoading: false,
    transfersReceivedError: null
  })),
  on(TransfersActions.listTransfersReceivedFailure, (state, { error }) => ({
    ...state,
    transfersReceivedLoading: false,
    transfersReceivedError: error
  })),

  on(TransfersActions.deleteTransferReceived, (state) => ({
    ...state,
    transferReceivedDeleting: true,
    transferReceivedDeleted: initialState.transferReceivedDeleted,
    transferReceivedDeletingError: initialState.transferReceivedDeletingError,
  })),
  on(TransfersActions.deleteTransferReceivedSuccess, (state) => ({
    ...state,
    transferReceivedDeleting: initialState.transferReceivedDeleting,
    transferReceivedDeleted: true,
  })),
  on(TransfersActions.deleteTransferReceivedFailure, (state, { error }) => ({
    ...state,
    transferReceivedDeletingError: error,
    transferReceivedDeleting: initialState.transferReceivedDeleting,
  })),

  on(TransfersActions.getReceipt, (state) => ({
    ...state,
    transferReceivedDetails: null,
    transferReceivedDetailsLoading: true,
    transferReceivedDetailsLoaded: false,
    transferReceivedDetailsError: null,
  })),
  on(TransfersActions.getReceiptSuccess, (state, { receipt }) => ({
    ...state,
    transferReceivedDetails: receipt,
    transferReceivedDetailsLoading: false,
    transferReceivedDetailsLoaded: true,
  })),
  on(TransfersActions.getReceiptFailure, (state, { error }) => ({
    ...state,
    transferReceivedDetailsLoading: false,
    transferReceivedDetailsLoaded: false,
    transferReceivedDetailsError: error,
  })),

  on(TransfersActions.listReceiptFiles, (state) => ({
    ...state,
    transferReceivedDetailsFiles: null,
    transferReceivedDetailsFilesLoading: true,
    transferReceivedDetailsFilesLoaded: false,
    transferReceivedDetailsFilesError: null,
  })),
  on(TransfersActions.listReceiptFilesSuccess, (state, { files }) => ({
    ...state,
    transferReceivedDetailsFiles: files,
    transferReceivedDetailsFilesLoading: false,
    transferReceivedDetailsFilesLoaded: true,
    transferReceivedDetailsFilesError: null,
  })),
  on(TransfersActions.listReceiptFilesFailure, (state, { error }) => ({
    ...state,
    transferReceivedDetailsFiles: null,
    transferReceivedDetailsFilesLoading: false,
    transferReceivedDetailsFilesLoaded: false,
    transferReceivedDetailsFilesError: error,
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return transfersReducer(state, action);
}
